<template>
  <div class="card timecard-page">
    <div class="card-body">
      <!-- Header -->
      <div class="tc-header">
        <div class="staff-name-header">
          <button class="btn btn-outline-secondary btn-sm" @click="$router.push({name:'CompanyStaffPage'})"><i class="fas fa-arrow-left mr-1"></i>戻る</button>
          <h5 class="staff-name-label">{{ data.name }}</h5>
          <button class="btn btn-success btn-sm btn-add-timecard" @click="openCreate"><i class="fas fa-plus mr-1"></i>打刻を追加</button>
        </div>
        <div class="month-selector">
          <div class="year-selector">
            <button class="year-nav-btn" @click="changeYear(-1)"><i class="fas fa-chevron-left"></i></button>
            <span class="year-label">{{ currentDate.getFullYear() }}年</span>
            <button class="year-nav-btn" @click="changeYear(1)"><i class="fas fa-chevron-right"></i></button>
          </div>
          <div class="month-btn-group">
            <button v-for="m in 12" :key="m" :class="['month-btn', { active: currentDate.getMonth() === m - 1 }]" @click="selectMonth(m - 1)">{{ m }}月</button>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="table-responsive">
        <table class="table table-hover tc-table">
          <thead>
            <tr>
              <th>日付</th>
              <th>種別</th>
              <th>出勤</th>
              <th>退勤</th>
              <th>休憩</th>
              <th>就業時間</th>
              <th>残業</th>
              <th>備考</th>
              <th>ステータス</th>
              <th class="th-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <!-- 1日1行。半日有給の日は打刻と有給が同じ行に載る（種別は有給を優先、
                 時刻・就業時間は打刻の値）。ステータスと操作は打刻・有給それぞれの
                 レコードに対して出す -->
            <tr v-for="row in sortedData" :key="row.key" :class="rowClass(row)">
              <td class="td-date">{{ row.date }}</td>
              <td><span :class="typeClass(row)">{{ typeLabel(row) }}</span></td>
              <td>{{ row.timecard ? row.timecard.time_start : '' }}</td>
              <td>{{ row.timecard ? row.timecard.time_stop : '' }}</td>
              <td>{{ row.timecard ? row.timecard.basic_break_hours : '' }}</td>
              <td>{{ row.timecard ? row.timecard.working_hours : '' }}</td>
              <td :class="row.timecard && row.timecard.overtime_hours ? 'td-overtime' : ''">{{ row.timecard ? (row.timecard.overtime_hours || '') : '' }}</td>
              <td class="td-notes">
                <span v-for="note in rowNotes(row)" :key="note.type" class="note-item" :title="note.text"><i class="fas fa-comment-alt mr-1 text-muted"></i>{{ truncateNote(note.text) }}</span>
              </td>
              <td>
                <div v-for="rec in rowRecords(row)" :key="rec.type" class="status-group">
                  <span v-if="row.timecard && row.leave" class="status-group-label">{{ rec.label }}</span>
                  <span :class="statusClass(rec.data.approval_status)">{{ rec.data.approval_status }}</span>
                </div>
              </td>
              <td class="td-actions" @click.stop>
                <div v-for="rec in rowRecords(row)" :key="rec.type" class="action-group">
                  <span v-if="row.timecard && row.leave" class="action-group-label">{{ rec.label }}</span>
                  <div class="action-btns">
                    <button v-if="rec.type === 'timecard'" class="btn btn-sm btn-edit" @click="openEdit(rec.data)" title="編集"><i class="fas fa-pen"></i></button>
                    <button v-if="rec.data.approval_status === '承認待ち'" class="btn btn-sm btn-approve" @click="updateApproval(rec, 2)" title="承認"><i class="fas fa-check"></i></button>
                    <button v-if="rec.data.approval_status === '承認待ち' || rec.data.approval_status === '承認済み'" class="btn btn-sm btn-revision" @click="updateApproval(rec, 3)" title="差し戻し"><i class="fas fa-undo"></i></button>
                    <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(rec)" title="削除"><i class="fas fa-trash"></i></button>
                  </div>
                </div>
              </td>
            </tr>
            <tr v-if="sortedData.length === 0">
              <td colspan="10" class="text-center text-muted py-4">データがありません</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- 削除確認モーダル -->
  <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
    <div class="delete-modal">
      <div class="delete-modal-body text-center py-4">
        <i class="fas fa-exclamation-triangle text-danger mb-3" style="font-size: 2rem;"></i>
        <p class="mb-1 font-weight-bold">本当に削除しますか？</p>
        <p class="text-muted mb-0" style="font-size:13px;">この操作は取り消せません。</p>
      </div>
      <div class="delete-modal-footer">
        <button class="btn btn-outline-secondary" @click="showDeleteModal = false">キャンセル</button>
        <button class="btn btn-danger" @click="deleteRecord">削除する</button>
      </div>
    </div>
  </div>

  <!-- 打刻の追加・修正（企業管理者） -->
  <TimecardEditModal
    v-if="editModal"
    :mode="editModal.mode"
    :timecard="editModal.timecard"
    :staff-options="staffOptions"
    :shop-options="shopOptions"
    :default-date="defaultDate"
    @close="editModal = null"
    @saved="onSaved"
  />
</template>

<script>
import TimecardEditModal from '../../components/TimecardEditModal.vue';
import { defaultEditDate } from '../../utils/timecardEdit';

export default {
  components: { TimecardEditModal },
  data() {
    return {
      data: { timecard: [], paid_leaves: [] },
      currentDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      company_plan: 1,
      showDeleteModal: false,
      deleteTarget: null,
      // 入力モーダル（{ mode: 'create' | 'edit', timecard }）。null なら閉じている
      editModal: null,
      // スタッフの所属店舗。打刻を追加するときの店舗の候補（一覧 API は店舗を返さず、兼務スタッフがいるため）
      shops: [],
    }
  },
  mounted() {
    this.loadData();
    this.loadShops();
  },
  computed: {
    // 1日1行にまとめる。半日有給は残り半日を勤務するため、同じ日に打刻と有給が
    // 並ぶ。以前は打刻と有給を別々の行にしていたため、有給の行の時刻が空になり
    // 「打刻が反映されていない」ように見えた（2026-09-04 の問い合わせ）。
    // 勤務カレンダーのモーダル・CSV/Excel と同じく、種別は有給を優先し、
    // 時刻・就業時間は打刻の値を残す。
    sortedData() {
      const byDate = new Map(); // date('n/j') -> [{ date, timecard, leave }, ...]
      const place = (slot, record) => {
        const list = byDate.get(record.date) || [];
        // 同じ日に同じ種類が2件以上ある場合（API は打刻を1日1件に名寄せするので
        // 通常は起きない）は、消さずに別の行として残す。
        let row = list.find(r => !r[slot]);
        if (!row) {
          row = { date: record.date, timecard: null, leave: null };
          list.push(row);
        }
        row[slot] = record;
        byDate.set(record.date, list);
      };
      (this.data.timecard || []).forEach(tc => place('timecard', tc));
      (this.data.paid_leaves || []).forEach(pl => place('leave', pl));

      const rows = [];
      byDate.forEach(list => list.forEach(r => {
        rows.push({
          ...r,
          key: r.date + ':' + (r.timecard ? 't' + r.timecard.id : '') + (r.leave ? 'l' + r.leave.id : ''),
        });
      }));
      const parseDate = (d) => {
        if (!d) return 0;
        const parts = String(d).split('/');
        return parts.length === 2 ? parseInt(parts[0]) * 100 + parseInt(parts[1]) : 0;
      };
      rows.sort((a, b) => parseDate(a.date) - parseDate(b.date));
      return rows;
    },
    staffOptions() {
      if (!this.data.id) return [];
      return [{ id: this.data.id, name: this.data.name, break_time: this.data.break_time }];
    },
    shopOptions() {
      return this.shops.map(s => ({ id: s.id, shop_name: s.shop_name }));
    },
    defaultDate() {
      return defaultEditDate(this.currentDate);
    },
  },
  methods: {
    formatDate() {
      return this.currentDate.getFullYear() + '-' + (this.currentDate.getMonth() + 1).toString().padStart(2, '0') + '-01';
    },
    changeYear(dir) {
      this.currentDate = new Date(this.currentDate.getFullYear() + dir, this.currentDate.getMonth(), 1);
      this.loadData();
    },
    selectMonth(m) {
      this.currentDate = new Date(this.currentDate.getFullYear(), m, 1);
      this.loadData();
    },
    loadData() {
      this.$http.get(process.env.VUE_APP_API_PATH + '/company/timecard/' + this.$route.params.staff_id + '/' + this.formatDate())
      .then(response => {
        this.data = response.data.data.staff;
        this.company_plan = response.data.data.company_plan?.[0] ?? 1;
      })
      .catch(error => { console.log(error); });
    },
    loadShops() {
      this.$http.get(process.env.VUE_APP_API_PATH + '/company/staff/' + this.$route.params.staff_id)
      .then(response => { this.shops = (response.data.data && response.data.data.shop) || []; })
      .catch(error => { console.log(error); });
    },
    openCreate() {
      this.editModal = { mode: 'create', timecard: null };
    },
    openEdit(timecard) {
      this.editModal = { mode: 'edit', timecard };
    },
    onSaved() {
      this.editModal = null;
      this.loadData();
    },
    statusClass(status) {
      if (status === '承認済み') return 'badge-status badge-approved';
      if (status === '承認待ち') return 'badge-status badge-pending';
      if (status === '修正依頼') return 'badge-status badge-revision';
      return 'badge-status';
    },
    // 種別は有給を優先。半日有給(leave_type=2)は全日の有給と区別して「半有」と出す。
    typeLabel(row) {
      if (!row.leave) return '通常';
      return Number(row.leave.leave_type) === 2 ? '半有' : '有給';
    },
    typeClass(row) {
      if (!row.leave) return 'badge-type badge-type-normal';
      return Number(row.leave.leave_type) === 2
        ? 'badge-type badge-type-half-leave'
        : 'badge-type badge-type-leave';
    },
    rowClass(row) {
      if (row.leave) return 'row-leave';
      if (row.timecard && row.timecard.overtime_hours && row.timecard.overtime_hours !== '00:00') return 'row-overtime';
      return '';
    },
    // 行に載っているレコード（打刻・有給）。ステータス・操作はこの単位で出す。
    // 戻り値の { type, data } は updateApproval() / confirmDelete() がそのまま受け取る。
    rowRecords(row) {
      const records = [];
      if (row.timecard) records.push({ type: 'timecard', label: '打刻', data: row.timecard });
      if (row.leave) records.push({ type: 'paid_leave', label: '有給', data: row.leave });
      return records;
    },
    rowNotes(row) {
      return this.rowRecords(row)
        .filter(rec => rec.data.notes)
        .map(rec => ({ type: rec.type, text: rec.data.notes }));
    },
    truncateNote(text) {
      return text.length > 10 ? text.slice(0, 10) + '...' : text;
    },
    updateApproval(entry, status) {
      const url = entry.type === 'timecard'
        ? process.env.VUE_APP_API_PATH + '/company/timecard/' + entry.data.id
        : process.env.VUE_APP_API_PATH + '/shop/paidLeave/' + entry.data.id;
      this.$http.put(url, { approval_status: status })
      .then(response => {
        if (response.data.success) {
          this.$toast.success(response.data.message);
          this.loadData();
        }
      })
      .catch(error => {
        if (error.response) this.$toast.error(error.response.data?.message || 'エラーが発生しました');
      });
    },
    confirmDelete(entry) {
      this.deleteTarget = entry;
      this.showDeleteModal = true;
    },
    deleteRecord() {
      if (!this.deleteTarget) return;
      const entry = this.deleteTarget;
      const url = entry.type === 'timecard'
        ? process.env.VUE_APP_API_PATH + '/shop/timecard/' + entry.data.id
        : process.env.VUE_APP_API_PATH + '/shop/paidLeave/' + entry.data.id;
      this.$http.delete(url)
      .then(response => {
        this.$toast.success(response.data.message || '削除しました');
        this.showDeleteModal = false;
        this.deleteTarget = null;
        this.loadData();
      })
      .catch(error => {
        this.$toast.error('削除に失敗しました');
        console.log(error);
      });
    },
  },
};
</script>

<style scoped>
.tc-header { margin-bottom: 16px; }
.staff-name-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.staff-name-label { font-size: 20px; font-weight: 700; margin: 0; color: #1a202c; }

.month-selector { margin-bottom: 12px; }
.year-selector { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 8px; }
.year-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: 1.5px solid #d2d6dc; background: #fff; color: #4a5568; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.year-nav-btn:hover { background: #049C4C; border-color: #049C4C; color: #fff; }
.year-label { font-size: 20px; font-weight: 700; min-width: 80px; text-align: center; }
.month-btn-group { display: flex; flex-wrap: wrap; justify-content: center; gap: 4px; }
.month-btn { padding: 6px 12px; font-size: 13px; font-weight: 600; border: 1.5px solid #d2d6dc; border-radius: 6px; background: #fff; color: #4a5568; cursor: pointer; transition: all 0.15s; min-width: 48px; }
.month-btn:hover { background: #edf2f7; border-color: #a0aec0; }
.month-btn.active { background: #049C4C; color: #fff; border-color: #049C4C; }

.tc-table { margin: 0; }
.tc-table thead th { font-size: 12px; font-weight: 700; color: #4a5568; background: #f7fafc; border-bottom: 2px solid #e2e8f0; padding: 10px 8px; white-space: nowrap; }
.tc-table tbody td { padding: 8px; font-size: 13px; vertical-align: middle; border-color: #edf2f7; }
.td-date { font-weight: 600; color: #1a202c; white-space: nowrap; }
.td-notes { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #718096; font-size: 12px; }
.th-actions { width: 160px; text-align: center; }
.td-actions { width: 160px; text-align: center; }
.btn-add-timecard { margin-left: auto; }
.btn-edit { background: #e2e8f0; color: #2d3748; border: 1px solid #cbd5e0; font-size: 11px; padding: 3px 7px; border-radius: 4px; transition: all 0.15s; }
.btn-edit:hover { background: #3182ce; color: #fff; border-color: #3182ce; }
.action-btns { display: flex; gap: 3px; justify-content: center; }
/* 打刻と有給が同じ行に載る日は、ステータス・操作を「打刻」「有給」のラベル付きで2段に出す */
.status-group { display: flex; align-items: center; gap: 4px; }
.status-group + .status-group { margin-top: 4px; }
.action-group { display: flex; align-items: center; justify-content: center; gap: 4px; }
.action-group + .action-group { margin-top: 4px; }
.status-group-label, .action-group-label { font-size: 10px; font-weight: 700; color: #718096; white-space: nowrap; }
.note-item { display: block; }
.td-overtime { color: #d69e2e; font-weight: 600; }

.row-leave { background-color: #fffaf0 !important; }
.row-overtime { background-color: #fffaf0 !important; }

.badge-status { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 4px; white-space: nowrap; }
.badge-approved { background: #c6f6d5; color: #22543d; }
.badge-pending { background: #fefcbf; color: #744210; }
.badge-revision { background: #fed7d7; color: #9b2c2c; }

.badge-type { font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 3px; }
.badge-type-normal { background: #edf2f7; color: #4a5568; }
.badge-type-leave { background: #fefcbf; color: #744210; }
/* 半日有給。全日の有給(黄)と見分けが付くようオレンジ寄りにする */
.badge-type-half-leave { background: #feebc8; color: #7b341e; }

.btn-approve { background: #c6f6d5; color: #22543d; border: 1px solid #9ae6b4; font-size: 11px; padding: 3px 7px; border-radius: 4px; transition: all 0.15s; }
.btn-approve:hover { background: #049C4C; color: #fff; border-color: #049C4C; }
.btn-revision { background: #fefcbf; color: #744210; border: 1px solid #f6e05e; font-size: 11px; padding: 3px 7px; border-radius: 4px; transition: all 0.15s; }
.btn-revision:hover { background: #d69e2e; color: #fff; border-color: #d69e2e; }

.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.4); z-index: 1050; display: flex; align-items: center; justify-content: center; }
.delete-modal { background: #fff; border-radius: 12px; width: 340px; max-width: 90vw; box-shadow: 0 20px 60px rgba(0,0,0,0.2); overflow: hidden; }
.delete-modal-footer { display: flex; justify-content: center; gap: 12px; padding: 12px 20px 20px; }
</style>
